import type { ShallowRef } from 'vue'
import type { SelectOption, SelectProps } from '@brighterly/lib-lumee-ui'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { whenSelected } from '@shared/lib/select'

import { usePaidStore } from '../store'

dayjs.extend(utc)
dayjs.extend(timezone)

export interface BlockSelectDateReturn {
  options: Ref<SelectOption[]>
  time: ComputedRef<string | undefined>
  disabled: ComputedRef<boolean>
  status: ShallowRef<SelectProps['status']>
  handleChange: (time: string | undefined) => void
}

export const useBlockSelectDate = (): BlockSelectDateReturn => {
  const paidStore = usePaidStore()

  const getOptions = (): SelectOption[] => {
    const bookingDate = toRaw(paidStore.getRescheduleDate)

    if (!bookingDate) {
      return []
    } else {
      const timeSlots = toRaw(paidStore.getAvailableSlots!)

      return timeSlots.map(slot => ({
        value: slot,
        text: dayjs(slot, DateFormat.Default, 'en', true).format(DateFormat.BookedTime),
      }))
    }
  }

  const options = ref<SelectOption[]>(getOptions())
  const status = shallowRef<SelectProps['status']>()
  const time = computed(() => paidStore.getRescheduleTime ?? undefined)
  const disabled = computed(() => paidStore.isProcessing || !paidStore.isRescheduleDate)

  const handleChange = whenSelected((value: string): void => {
    paidStore.setRescheduleTime(value)
    status.value = undefined
  })

  watch(
    () => paidStore.getTriggerRescheduleSelect,
    () => {
      status.value = paidStore.isRescheduleTime ? undefined : 'error'
    },
  )

  return {
    options,
    time,
    disabled,
    status,
    handleChange,
  }
}
